import { Button, Checkbox, cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  ArrowClockwise as ArrowClockwiseIcon,
  ArrowCounterClockwise as ArrowCounterClockwiseIcon,
  Cube as CubeIcon,
  FileText as FileTextIcon,
  FloppyDisk as FloppyDiskIcon,
  Trash as TrashIcon,
  Users as UsersIcon,
  WarningCircle as WarningCircleIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";

type QueueItem = {
  id: string;
  /** A standalone maquette (N110) or a compte-rendu (N142) is listed like a
   * document. */
  kind: "document" | "maquette" | "compte_rendu";
  /** Version id, or compte-rendu id for a compte-rendu. */
  versionId: number;
  fullname: string | null;
  hasNative: boolean;
  hasPdf: boolean;
  hasIfc: boolean;
  maquette: {
    versionId: number;
    fullname: string | null;
    name: string | null;
    hasNative: boolean;
    hasIfc: boolean;
  } | null;
  inBordereau: boolean;
  /** Recipients proposed from the previous diffusion. */
  recipientCount: number;
};

/** A recipient of the previous diffusion who no longer intervenes. */
type ReplacementWarning = {
  versionId: number;
  fullname: string | null;
  formerTeamName: string;
  replacementTeamNames: string[];
};

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; items: QueueItem[] };

function formatsLabel(item: QueueItem): string {
  const formats: string[] = [];
  if (item.hasNative) formats.push("Natif");
  if (item.hasPdf) formats.push("PDF");
  if (item.hasIfc) formats.push("IFC");
  return formats.join(" / ");
}

function recipientsTitle(count: number): string {
  return count > 1
    ? `${count} destinataires repris de la diffusion précédente`
    : "1 destinataire repris de la diffusion précédente";
}

function maquetteFormatsLabel(
  maquette: NonNullable<QueueItem["maquette"]>,
): string {
  const formats: string[] = [];
  if (maquette.hasNative) formats.push("Natif");
  if (maquette.hasIfc) formats.push("IFC");
  return formats.join(" / ");
}

function maquetteTitle(maquette: NonNullable<QueueItem["maquette"]>): string {
  const label = maquette.fullname ?? maquette.name;
  return isNil(label) ? "Maquette associée" : `Maquette associée : ${label}`;
}

function warningMessage(warning: ReplacementWarning): string {
  const document = warning.fullname ?? "(Sans nom)";
  const replacements = warning.replacementTeamNames.join(", ");
  return warning.replacementTeamNames.length > 0
    ? `${document} : ${warning.formerTeamName} n'intervient plus, remplacé par ${replacements}.`
    : `${document} : ${warning.formerTeamName} n'intervient plus, aucun remplaçant trouvé.`;
}

type DiffusionQueuePageProps = {
  /** Panel heading, e.g. "Documents à diffuser". */
  title: string;
  /** Diffusion target whose waiting list is shown. */
  target: "do" | "presta";
  /**
   * Enables the draft workflow (staged removals with undo/redo, durable
   * "Enregistrer le brouillon") and the selection actions. Off by default so
   * a caller can render the plain read-only list without the draft controls.
   */
  enableDraftWorkflow?: boolean;
  /** Refetch signal from the bordereau panel (a document came back to the
   * list, or the bordereau was validated). */
  reloadKey?: number;
  /** The bordereau panel holds unsaved edits, which its reload after an
   * addition would discard. */
  bordereauDirty?: boolean;
  /** Called once documents were added to the bordereau en construction. */
  onBordereauChange?: () => void;
};

/**
 * Waiting list "Documents à diffuser" of screens 019 (donneurs d'ordre) and
 * 020 (prestataires): the document and standalone maquette versions the
 * current user pilots that were put in diffusion toward the target. A row
 * carries a recipients pictogram when the previous diffusion proposes some.
 *
 * When `enableDraftWorkflow` is set the user can:
 * - select one or several documents (per-row checkbox + "tout sélectionner"
 *   header checkbox) and **Retirer de la liste**, with **annuler / rétablir**
 *   (undo/redo) of those removals entirely client-side, then **Enregistrer le
 *   brouillon** to persist them (DELETE …/diffusion-queue);
 * - send the selection to the bordereau en construction (POST
 *   …/diffusion-bordereau/documents), keeping it in the list or not. The
 *   bordereau proposes recipients and formats from the previous diffusion and
 *   warns here about recipients who no longer intervene. Documents already in
 *   the bordereau are faded and not selectable.
 */
export function DiffusionQueuePage({
  title,
  target,
  enableDraftWorkflow = false,
  reloadKey,
  bordereauDirty = false,
  onBordereauChange,
}: DiffusionQueuePageProps) {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });

  // Draft state: `removed` is the set of queue-item ids the user has staged
  // for removal; `past`/`future` are the undo/redo snapshots of that set. All
  // three reset once a save makes the server the new baseline.
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [past, setPast] = useState<Set<string>[]>([]);
  const [future, setFuture] = useState<Set<string>[]>([]);
  const [busy, setBusy] = useState(false);
  // Multi-select: ids of the visible rows the user has ticked, for a single
  // "Retirer de la liste" removal step or an add to the bordereau.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [warnings, setWarnings] = useState<ReplacementWarning[]>([]);

  const loadQueue = useCallback(async () => {
    if (isNil(yardId)) return;
    setState((prev) =>
      prev.status === "success" ? prev : { status: "loading" },
    );
    // openapi-fetch rejects on network/transport failures (only HTTP-level
    // errors come back as `error`), so guard the await or the page hangs on
    // "Chargement..." forever.
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/diffusion-queue",
        { params: { path: { yardId }, query: { target } } },
      );
      if (error || isNil(data)) {
        setState({
          status: "error",
          error: "Erreur lors du chargement de la liste de diffusion",
        });
        return;
      }
      setState({ status: "success", items: data.data.items });
    } catch {
      setState({
        status: "error",
        error: "Erreur lors du chargement de la liste de diffusion",
      });
    }
  }, [yardId, target]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue, reloadKey]);

  const resetDraft = useCallback(() => {
    setRemoved(new Set());
    setPast([]);
    setFuture([]);
    setSelectedIds(new Set());
  }, []);

  // Stage the currently selected rows as one removal step (single undo entry),
  // then clear the selection.
  const removeSelected = useCallback(() => {
    if (selectedIds.size === 0) return;
    setPast((p) => [...p, removed]);
    setFuture([]);
    setRemoved(new Set([...removed, ...selectedIds]));
    setSelectedIds(new Set());
  }, [selectedIds, removed]);

  const undo = useCallback(() => {
    const previous = past.at(-1);
    if (isNil(previous)) return;
    setFuture((f) => [removed, ...f]);
    setPast((p) => p.slice(0, -1));
    setRemoved(previous);
  }, [past, removed]);

  const redo = useCallback(() => {
    const next = future[0];
    if (isNil(next)) return;
    setPast((p) => [...p, removed]);
    setFuture((f) => f.slice(1));
    setRemoved(next);
  }, [future, removed]);

  const allItems = state.status === "success" ? state.items : [];
  const items = enableDraftWorkflow
    ? allItems.filter((item) => !removed.has(item.id))
    : allItems;
  const total = items.length;
  const columnCount = 3 + (enableDraftWorkflow ? 1 : 0);

  const isDirty = enableDraftWorkflow && removed.size > 0;
  useUnsavedChangesGuard(isDirty);

  // Selection is over the visible (not-yet-removed) rows that are not already
  // in the bordereau.
  const selectable = items.filter((item) => !item.inBordereau);
  const selectedCount = selectable.filter((item) =>
    selectedIds.has(item.id),
  ).length;
  const allSelected =
    selectable.length > 0 && selectedCount === selectable.length;

  const toggleOne = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds(
      allSelected ? new Set() : new Set(selectable.map((item) => item.id)),
    );
  }, [selectable, allSelected]);

  const save = useCallback(async () => {
    if (isNil(yardId)) return;
    const removedItems = allItems.filter((item) => removed.has(item.id));
    if (removedItems.length === 0) return;
    const idsOf = (kind: QueueItem["kind"]) =>
      removedItems
        .filter((item) => item.kind === kind)
        .map((item) => item.versionId);
    setBusy(true);
    try {
      const { error } = await apiClient.DELETE(
        "/api/yards/{yardId}/diffusion-queue",
        {
          params: { path: { yardId } },
          body: {
            target,
            versionIds: idsOf("document"),
            maquetteVersionIds: idsOf("maquette"),
            compteRenduIds: idsOf("compte_rendu"),
          },
        },
      );
      if (error) {
        toaster.error({
          title: "Erreur",
          description: "Impossible d'enregistrer le brouillon.",
        });
        return;
      }
      toaster.success({
        title: "Brouillon enregistré",
        description: "Les modifications ont été enregistrées.",
      });
      resetDraft();
      await loadQueue();
    } catch {
      toaster.error({
        title: "Erreur",
        description: "Impossible d'enregistrer le brouillon.",
      });
    } finally {
      setBusy(false);
    }
  }, [yardId, removed, allItems, target, resetDraft, loadQueue]);

  const addToBordereau = useCallback(
    async (keep: boolean) => {
      if (isNil(yardId)) return;
      const selected = selectable.filter((item) => selectedIds.has(item.id));
      if (selected.length === 0) return;
      const idsOf = (kind: QueueItem["kind"]) =>
        selected
          .filter((item) => item.kind === kind)
          .map((item) => item.versionId);
      setBusy(true);
      try {
        const { data, error } = await apiClient.POST(
          "/api/yards/{yardId}/diffusion-bordereau/documents",
          {
            params: { path: { yardId } },
            body: {
              target,
              versionIds: idsOf("document"),
              maquetteVersionIds: idsOf("maquette"),
              compteRenduIds: idsOf("compte_rendu"),
              keep,
            },
          },
        );
        if (error || isNil(data)) {
          toaster.error({
            title: "Erreur",
            description: "Impossible d'ajouter la sélection au bordereau.",
          });
          return;
        }
        toaster.success({
          title: "Sélection ajoutée au bordereau",
          description: keep
            ? "Les documents restent dans la liste d'attente."
            : "Les documents ont été retirés de la liste d'attente.",
        });
        setWarnings(data.data.warnings);
        setSelectedIds(new Set());
        await loadQueue();
        onBordereauChange?.();
      } catch {
        toaster.error({
          title: "Erreur",
          description: "Impossible d'ajouter la sélection au bordereau.",
        });
      } finally {
        setBusy(false);
      }
    },
    [yardId, target, selectable, selectedIds, loadQueue, onBordereauChange],
  );
  const addDisabled = busy || selectedCount === 0 || isDirty || bordereauDirty;
  const addTitle =
    isDirty || bordereauDirty
      ? "Enregistrez ou annulez vos modifications avant d'ajouter au bordereau"
      : undefined;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {title}
          </h2>
          {state.status === "success" && (
            <p className="mt-1 text-sm text-zinc-500">
              {total} document{total > 1 ? "s" : ""} en attente de diffusion
            </p>
          )}
        </div>

        {enableDraftWorkflow && (
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              outline
              onClick={undo}
              disabled={busy || past.length === 0}
              title="Annuler la dernière action"
            >
              <ArrowCounterClockwiseIcon size={16} />
              Annuler
            </Button>
            <Button
              type="button"
              outline
              onClick={redo}
              disabled={busy || future.length === 0}
              title="Rétablir l'action annulée"
            >
              <ArrowClockwiseIcon size={16} />
              Rétablir
            </Button>
            <Button
              type="button"
              outline
              onClick={save}
              disabled={busy || !isDirty}
            >
              <FloppyDiskIcon size={16} />
              Enregistrer le brouillon
            </Button>
          </div>
        )}
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{state.error}</p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <div className="h-full overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            {/*
              Screen-design rules: fixed table layout so the fixed columns keep
              a constant width, the Document column consumes the remaining
              width, rows keep a fixed height and truncate (full text on hover),
              and there is no horizontal or page-level scroll at 1280×800 — only
              the table body scrolls vertically under a sticky header.
            */}
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                {enableDraftWorkflow && <col className="w-10" />}
                <col />
                <col className="w-[calc(11ch+24px)]" />
                <col className="w-28" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  {enableDraftWorkflow && (
                    <th className="px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                      <Checkbox
                        checked={
                          allSelected
                            ? true
                            : selectedCount > 0
                              ? "indeterminate"
                              : false
                        }
                        disabled={busy || selectable.length === 0}
                        onCheckedChange={toggleAll}
                        aria-label="Tout sélectionner"
                      />
                    </th>
                  )}
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Document
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Formats
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Maquette
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.status === "loading" ? (
                  <tr>
                    <td
                      colSpan={columnCount}
                      className="px-3 py-8 text-center text-sm text-zinc-500"
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columnCount}
                      className="px-3 py-12 text-center text-sm text-zinc-500"
                    >
                      Aucun document en attente de diffusion
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const formats = formatsLabel(item);
                    return (
                      <tr
                        key={item.id}
                        className={cn(
                          "h-10 border-b border-zinc-100 last:border-0 dark:border-zinc-800",
                          item.inBordereau && "opacity-50",
                        )}
                        title={
                          item.inBordereau
                            ? "Déjà dans le bordereau en construction"
                            : undefined
                        }
                      >
                        {enableDraftWorkflow && (
                          <td className="px-3 text-center">
                            <Checkbox
                              checked={selectedIds.has(item.id)}
                              disabled={busy || item.inBordereau}
                              onCheckedChange={() => toggleOne(item.id)}
                              aria-label={`Sélectionner ${item.fullname ?? "(Sans nom)"}`}
                            />
                          </td>
                        )}
                        <td className="px-3 text-left">
                          <div className="flex min-w-0 items-center gap-1.5">
                            {item.kind === "maquette" && (
                              <CubeIcon
                                size={16}
                                className="shrink-0 text-zinc-500"
                                aria-label="Maquette"
                              />
                            )}
                            {item.kind === "compte_rendu" && (
                              <FileTextIcon
                                size={16}
                                className="shrink-0 text-zinc-500"
                                aria-label="Compte-rendu, pièces jointes comprises"
                              />
                            )}
                            <span
                              className="truncate"
                              title={item.fullname ?? undefined}
                            >
                              {item.fullname ?? "(Sans nom)"}
                            </span>
                            {item.recipientCount > 0 && (
                              <span
                                className="ml-auto inline-flex shrink-0 items-center gap-0.5 text-zinc-600 dark:text-zinc-400"
                                title={recipientsTitle(item.recipientCount)}
                              >
                                <UsersIcon
                                  size={16}
                                  weight="fill"
                                  aria-hidden
                                />
                                {item.recipientCount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td
                          className={cn(
                            "truncate whitespace-nowrap px-3 text-center",
                            formats.length === 0 && "text-zinc-300",
                          )}
                          title={formats || undefined}
                        >
                          {formats || "-"}
                        </td>
                        <td className="truncate whitespace-nowrap px-3 text-center">
                          {isNotNil(item.maquette) ? (
                            <span
                              className="inline-flex items-center justify-center gap-1 text-zinc-700 dark:text-zinc-300"
                              title={maquetteTitle(item.maquette)}
                            >
                              <CubeIcon size={16} aria-hidden />
                              {maquetteFormatsLabel(item.maquette) || "-"}
                            </span>
                          ) : (
                            <span className="text-zinc-300">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {warnings.length > 0 && (
        <div
          role="alert"
          className="mx-4 mb-2 flex max-h-32 shrink-0 items-start gap-2 overflow-y-auto rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <WarningCircleIcon
            size={18}
            className="mt-0.5 shrink-0"
            aria-hidden
          />
          <ul className="min-w-0 flex-1 space-y-1">
            {warnings.map((warning) => (
              <li
                key={`${warning.versionId}:${warning.fullname}:${warning.formerTeamName}`}
              >
                {warningMessage(warning)}
              </li>
            ))}
          </ul>
          <Button type="button" plain onClick={() => setWarnings([])}>
            Fermer
          </Button>
        </div>
      )}

      {enableDraftWorkflow && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-zinc-200 px-4 py-3 dark:border-zinc-700">
          <span className="text-sm text-zinc-500">Pour la sélection :</span>
          <Button
            type="button"
            outline
            onClick={removeSelected}
            disabled={busy || selectedCount === 0}
          >
            <TrashIcon size={16} />
            Retirer de la liste
            {selectedCount > 0 ? ` (${selectedCount})` : ""}
          </Button>
          <Button
            type="button"
            onClick={() => addToBordereau(true)}
            disabled={addDisabled}
            title={addTitle}
          >
            Ajouter sélection à bordereau et conserver
          </Button>
          <Button
            type="button"
            onClick={() => addToBordereau(false)}
            disabled={addDisabled}
            title={addTitle}
          >
            Ajouter sélection à bordereau et retirer
          </Button>
        </div>
      )}
    </div>
  );
}
